import * as React from 'react'
import {useMemo} from 'react'
import {
    GridCallbackDetails,
    GridColDef,
    GridFilterItem,
    GridFilterModel,
    GridRenderCellParams,
    GridRowId,
    GridRowSelectionModel,
    GridSortModel
} from '@mui/x-data-grid'
import './SearchResultDataTable.scss'
import {SearchItem} from '../../../model/SearchItem'
import {IdType} from '../../../../util/models/IdType'
import {TextWithTitle} from '../../../../genericComponents/typographie/TextWithTitle'
import {ItemRowHeaderCard} from '../ItemRowCard/ItemRowHeaderCard'
import {ItemTypeIcon} from '../ItemRowCard/ItemMainProperties/ItemTypeIcon'
import {ItemDefinedByComponent} from '../ItemRowCard/ItemMainProperties/ItemDefinedByComponent'
import ItemCardProperty from '../ItemRowCard/generic/ItemRowCardProperty'
import DaTXDataGrid from '../../../../genericComponents/grid/DaTXDataGrid'
import {
    equalsOperatorWithExternalValues,
    matchStringOperatorInputWithMinimumCharacters
} from '../../../../genericComponents/grid/gridFilterOperators'
import {validate as uuidValidate} from 'uuid'
import {
    ItemTechnicalClassificationComponent
} from '../ItemRowCard/ItemMainProperties/ItemTechnicalClassificationComponent'
import {SortingDirection, SortingField} from '../../../model/Sorting'
import {DisplayFiltersDTO} from '../../../model/DisplayFiltersDTO'
import {FullSearchParams, SearchParamTools} from '../../useFullSearchParams'

interface FilterManager {
    prefilledFilter: (fullSearchParams: FullSearchParams) => GridFilterItem[],
    updateFilter: (filters: GridFilterItem[], fullSearchParams: FullSearchParams) => boolean,
    resetFilter: (fullSearchParams: FullSearchParams) => boolean
}

const updateSearchParam = (newValue: string | undefined, shouldUpdate: boolean, searchParamTools: SearchParamTools) => {
    if (shouldUpdate && newValue) {
        searchParamTools.setValue(newValue)
        return true
    }
    if (!newValue && searchParamTools.exists()) {
        searchParamTools.resetValue()
        return true
    }
    return false
}

const filterManagerMapping: Map<SortingField, FilterManager> = new Map([
    [SortingField.PART_NUMBER, {
        prefilledFilter: (fullSearchParams) => {
            const {partNumberPhrase} = fullSearchParams
            if (partNumberPhrase) {
                return [{
                    id: SortingField.PART_NUMBER,
                    field: SortingField.PART_NUMBER,
                    operator: 'matchString',
                    value: [partNumberPhrase]
                }]
            }
            return []
        },
        updateFilter: (filters, fullSearchParams) => {
            const {partNumberPhrase, partNumberTools} = fullSearchParams
            const newPartNumberPhrase = filters.find(filter => filter.field === SortingField.PART_NUMBER)?.value[0]
            const shouldUpdate = newPartNumberPhrase && newPartNumberPhrase != partNumberPhrase
            return updateSearchParam(newPartNumberPhrase, shouldUpdate, partNumberTools)
        },
        resetFilter: (fullSearchParams) => {
            return fullSearchParams.partNumberTools.resetValue()
        }
    }],
    [SortingField.NAME_ITEM, {
        prefilledFilter: (fullSearchParams) => {
            const {labelPhrase} = fullSearchParams
            if (labelPhrase) {
                return [{
                    id: SortingField.NAME_ITEM,
                    field: SortingField.NAME_ITEM,
                    operator: 'matchString',
                    value: [labelPhrase]
                }]
            }
            return []
        },
        updateFilter: (filters, fullSearchParams) => {
            const {labelPhrase, labelTools} = fullSearchParams
            const newLabelPhrase = filters.find(filter => filter.field === SortingField.NAME_ITEM)?.value[0]
            const shouldUpdate = newLabelPhrase && newLabelPhrase != labelPhrase
            return updateSearchParam(newLabelPhrase, shouldUpdate, labelTools)
        },
        resetFilter: (fullSearchParams) => {
            return fullSearchParams.labelTools.resetValue()
        }
    }],
    [SortingField.ID, {
        prefilledFilter: (fullSearchParams) => {
            const {idPhrase} = fullSearchParams
            if (idPhrase) {
                return [{
                    id: SortingField.ID,
                    field: SortingField.ID,
                    operator: 'matchString',
                    value: [idPhrase]
                }]
            }
            return []
        },
        updateFilter: (filters, fullSearchParams) => {
            const {idPhrase, idTools} = fullSearchParams
            const newIdPhrase = filters.find(filter => filter.field === SortingField.ID)?.value[0]
            const shouldUpdate = newIdPhrase && newIdPhrase != idPhrase
            return updateSearchParam(newIdPhrase, shouldUpdate, idTools)
        },
        resetFilter: (fullSearchParams) => {
            return fullSearchParams.idTools.resetValue()
        }
    }],
    [SortingField.NAME_DEFINED_BY, {
        prefilledFilter: (fullSearchParams) => {
            const {organizations} = fullSearchParams
            if (organizations?.organizationIds?.length) {
                return [{
                    id: SortingField.NAME_DEFINED_BY,
                    field: SortingField.NAME_DEFINED_BY,
                    operator: 'matchString',
                    value: organizations.organizationIds
                }]
            }
            return []
        },
        updateFilter: (filters, fullSearchParams) => {
            const {organizations, organizationsTools} = fullSearchParams
            const newSearchOrganizationFilter = filters.find(filter => filter.field === SortingField.NAME_DEFINED_BY)?.value
            console.log('new', newSearchOrganizationFilter)
            console.log('organizations', organizations)
            const shouldUpdate = newSearchOrganizationFilter?.length && JSON.stringify(newSearchOrganizationFilter) !== JSON.stringify(organizations?.organizationIds)
            const newFilter = newSearchOrganizationFilter?.length ? JSON.stringify({organizationIds: newSearchOrganizationFilter}) : undefined
            return updateSearchParam(newFilter, shouldUpdate, organizationsTools)
        },
        resetFilter: (fullSearchParams) => {
            return fullSearchParams.organizationsTools.resetValue()
        }
    }],
    [SortingField.NAME_TYPE_NODE, {
        prefilledFilter: (fullSearchParams) => {
            const {itemTypes} = fullSearchParams
            if (itemTypes?.itemTypes?.length) {
                return [{
                    id: SortingField.NAME_TYPE_NODE,
                    field: SortingField.NAME_TYPE_NODE,
                    operator: '"equals"',
                    value: itemTypes.itemTypes
                }]
            }
            return []
        },
        updateFilter: (filters, fullSearchParams) => {
            const {itemTypes, itemTypesTools} = fullSearchParams
            const newSearchItemTypeFilter = filters.find(filter => filter.field === SortingField.NAME_TYPE_NODE)?.value
            const shouldUpdate = newSearchItemTypeFilter?.length && JSON.stringify(newSearchItemTypeFilter.sort()) !== JSON.stringify(itemTypes?.itemTypes.sort())
            const newFilter = newSearchItemTypeFilter?.length ? JSON.stringify({itemTypes: newSearchItemTypeFilter}) : undefined
            return updateSearchParam(newFilter, shouldUpdate, itemTypesTools)
        },
        resetFilter: (fullSearchParams) => {
            return fullSearchParams.itemTypesTools.resetValue()
        }
    }]
])

export default function SearchResultDataTable({
                                                  definitionsToDisplay,
                                                  list,
                                                  handleAddSingleItemToList,
                                                  highlightPhrase,
                                                  showAddToListButton = true,
                                                  noItemMessages,
                                                  selectedItems,
                                                  handleRowSelectionChange,
                                                  enableColumFiltering = false,
                                                  isLoading,
                                                  filterValues,
                                                  fullSearchParams
                                              }: {
    definitionsToDisplay: { id: IdType, name: string }[],
    list: SearchItem[],
    handleAddSingleItemToList: (item: SearchItem) => void
    highlightPhrase?: string | null,
    showAddToListButton?: boolean,
    noItemMessages?: string,
    selectedItems?: Set<GridRowId>,
    handleRowSelectionChange?: (newSelectionModel: GridRowSelectionModel, details: GridCallbackDetails) => void
    enableColumFiltering?: boolean,
    isLoading?: boolean,
    filterValues?: DisplayFiltersDTO,
    fullSearchParams: FullSearchParams
}) {
    const {
        phrase,
        organizations,
        itemTypes,
        partNumberPhrase,
        labelPhrase,
        idPhrase,
        sortingDefinitionId,
        sortingDirection,
        sortingField,
        applySearchParams,
        sortingTools: {
            setSorting,
            resetSorting
        }
    } = fullSearchParams

    const isSortingByDefinition = (newField: string | null) => newField && uuidValidate(newField)
    const gridSortingField = (sortingField === SortingField.DEFINITION_VALUE && isSortingByDefinition(sortingDefinitionId)) ? sortingDefinitionId! : sortingField
    const sortingModel: GridSortModel = [{field: gridSortingField, sort: sortingDirection}]

    const getPrecompiledFilters = (): GridFilterModel => {
        const items: GridFilterItem[] = []
        filterManagerMapping.forEach(value => {
            items.push(...value.prefilledFilter(fullSearchParams))
        })
        return {items}
    }

    const filterModel: GridFilterModel = useMemo(() => {
        if (!enableColumFiltering) return {items: []}
        return getPrecompiledFilters()
    }, [phrase, organizations, itemTypes, partNumberPhrase, labelPhrase, idPhrase])

    const applyCustomFilters = ({filters}: { filters: GridFilterItem[] }) => {
        if (!enableColumFiltering) return

        if (filters.length < 1) return resetFilters()

        let isSearchParamUpdated = false
        filterManagerMapping.forEach((value) => {
            if (value.updateFilter(filters, fullSearchParams)) isSearchParamUpdated = true
        })
        if (isSearchParamUpdated) applySearchParams()
    }

    const resetFilters = () => {
        let isSearchParamUpdated = false
        filterManagerMapping.forEach((value) => {
            if (value.resetFilter(fullSearchParams)) isSearchParamUpdated = true
        })
        if (isSearchParamUpdated) applySearchParams()
    }


    const handleSortModelChange = (model: GridSortModel, _: GridCallbackDetails) => {
        if (model.length < 1) return resetSorting()

        const params = model[0]
        const newField = params.field as SortingField
        const direction = params.sort as SortingDirection

        setSorting(newField, direction)
    }

    const columns: GridColDef<SearchItem>[] = useMemo(() => [
        {
            flex: 1, minWidth: 360,
            field: SortingField.PART_NUMBER,
            headerName: 'Part Number',
            sortable: true,
            valueGetter: (value, row) => row?.partNumber ?? '',
            renderCell: (params: GridRenderCellParams) => <ItemRowHeaderCard item={params.row} key={params.row.id}
                                                                             handleAddSingleItemToList={handleAddSingleItemToList}
                                                                             highlightPhrase={highlightPhrase}
                                                                             showAddToListButton={showAddToListButton}
            />,
            filterOperators: [matchStringOperatorInputWithMinimumCharacters],
            filterable: enableColumFiltering
        },
        {
            flex: 2, minWidth: 200,
            field: SortingField.NAME_DEFINED_BY,
            headerName: 'Defined By',
            sortable: true,
            valueGetter: (value, row) => row?.definedByOrganization?.label ?? '',
            renderCell: (params: GridRenderCellParams) => <ItemDefinedByComponent
                definedByOrganization={params.row.definedByOrganization}/>,
            filterOperators: [equalsOperatorWithExternalValues(filterValues?.organizationFilter?.values)],
            filterable: enableColumFiltering
        },
        {
            flex: 1, minWidth: 200,
            field: SortingField.NAME_ITEM,
            headerName: 'Name',
            sortable: true,
            valueGetter: (value, row) => row?.label ?? '',
            renderCell: (params: GridRenderCellParams) => <ItemCardProperty value={params.value}
                                                                            dataTestId={'datx-label-property'}/>,
            filterOperators: [matchStringOperatorInputWithMinimumCharacters],
            filterable: enableColumFiltering

},
        {
            flex: 2, minWidth: 160,
            field: SortingField.ID,
            headerName: 'DATX ID',
            sortable: true,
            valueGetter: (value, row) => row.id ?? '',
            renderCell: (params: GridRenderCellParams) => <ItemCardProperty value={params.value}
                                                                            dataTestId={'datx-id-property'}/>,
            filterOperators: [matchStringOperatorInputWithMinimumCharacters],
            filterable: enableColumFiltering
        },
        {
            flex: 2, minWidth: 200,
            field: SortingField.NAME_TECHNICAL_NODE,
            headerName: 'Technical Classification',
            disableColumnMenu: true,
            sortable: true,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => (
                <ItemTechnicalClassificationComponent item={params.row}/>
            )
        },
        {
            flex: 1, minWidth: 96,
            field: SortingField.NAME_TYPE_NODE,
            headerName: 'Item Type',
            sortable: true,
            filterOperators: [equalsOperatorWithExternalValues(filterValues?.itemTypeFilter?.values)],
            filterable: enableColumFiltering,
            valueGetter: (value, row) => row.type?.label ?? '',
            renderCell: (params: GridRenderCellParams) => (
                <ItemTypeIcon itemType={params.row.type}/>
            )
        }
    ], [filterValues])

    if (definitionsToDisplay) {
        columns.push(...definitionsToDisplay.map(definition => ({
            flex: 1, minWidth: 160,
            headerName: definition.name,
            field: definition.id,
            sortable: true,
            filterable: false,
            renderCell: (params: GridRenderCellParams) => {
                const retrievedAttribute = params.row.attributes.find((attribute: any) => attribute.attributeDefinitionId === definition.id)
                if (retrievedAttribute?.value && retrievedAttribute.value.value)
                    return <TextWithTitle
                        text={retrievedAttribute.value.value + ' ' + (retrievedAttribute.unit ?? '')}/>
                else return null
            }
        })))
    }

    return (
        <DaTXDataGrid
            getRowId={(list: SearchItem) => list?.id}
            applyFilters={applyCustomFilters}
            rows={list}
            columns={columns}
            hideFooter={true}
            getRowClassName={(params) => 'item-search-data-table-row'}
            getCellClassName={(params) => 'item-search-data-table-cell'}
            displayActionBar={false}
            checkboxSelection={true}
            rowSelectionModel={Array.from(selectedItems ?? [])}
            onRowSelectionModelChange={handleRowSelectionChange}
            noItemMessages={noItemMessages}
            sortingModel={sortingModel}
            onSortModelChange={handleSortModelChange}
            isLoading={isLoading}
            filterModel={filterModel}
        >
        </DaTXDataGrid>
    )
}
